import { merge } from '../../utils/UNSAFE_stringUtils';
import { useUser } from '../UNSAFE_useUser';

type Props = {
  baseId: string;
  hasEndContent?: boolean;
  hasInsideLabel?: boolean;
  hasStartContent?: boolean;
  hasValue?: boolean;
  isDisabled?: boolean;
  isFocused?: boolean;
  labelId?: string;
  prefix?: string;
  suffix?: string;
  value?: string;
};

type PrefixSuffixVariant = 'prefix' | 'suffix';

// returns the prefix and/or suffix components
// if the condition in which they should be shown is met.
export const usePrefixSuffix = ({
  baseId,
  hasEndContent,
  hasInsideLabel,
  hasStartContent,
  hasValue,
  isDisabled,
  isFocused,
  labelId,
  prefix,
  suffix,
  value
}: Props) => {
  const { direction } = useUser();
  // for accessibility, if you have a prefix, then you must have a prefixId
  const prefixId = `${baseId}-prefix`;
  const suffixId = `${baseId}-suffix`;
  const hasPrefixText = prefix !== undefined && prefix !== '';
  const hasSuffixText = suffix !== undefined && suffix !== '';
  const shouldRenderPrefix = hasPrefixText && !isDisabled && (hasValue || isFocused);
  const shouldRenderSuffix = hasSuffixText && !isDisabled && (hasValue || isFocused);

  // get value and prefix/suffix if available and return as plain text.
  const valuePrefixSuffix =
    hasPrefixText || hasSuffixText ? getText(direction, hasValue, prefix, suffix, value) : value;
  // get aria-labelledby if there is a prefix or suffix text
  const ariaLabelledBy =
    hasPrefixText || hasSuffixText
      ? merge([labelId, hasPrefixText ? prefixId : undefined, hasSuffixText ? suffixId : undefined])
      : undefined;
  const prefixProps = {
    id: prefixId,
    hasEndContent,
    hasInsideLabel,
    hasStartContent,
    isDisabled,
    isFocused,
    text: prefix ?? '',
    variant: 'prefix' as PrefixSuffixVariant
  };
  const suffixProps = {
    id: suffixId,
    hasInsideLabel,
    isDisabled,
    isFocused,
    text: suffix ?? '',
    variant: 'suffix' as PrefixSuffixVariant
  };

  return {
    shouldRenderPrefix,
    shouldRenderSuffix,
    prefixProps,
    suffixProps,
    valuePrefixSuffix,
    ariaLabelledBy
  };
};

/**
 * Returns the value to render in plain text, taking into account the prefix and suffix
 * and reading direction. Do not add a prefix/suffix if the component doesn't have a value.
 * @returns string | undefined
 */
function getText(
  direction: 'ltr' | 'rtl',
  hasValue?: boolean,
  prefix?: string,
  suffix?: string,
  value?: string
) {
  // Do not add a prefix/suffix if the component doesn't have a value.
  if (!hasValue) {
    return value;
  }
  // TODO: direction is 'ltr' even though the storybook is rtl.
  // Test this when the storybook bug is fixed. ?src=confmacro
  const isLTR = direction === 'ltr';
  const plainPrefix = prefix === undefined ? `` : isLTR ? `${prefix} ` : ` ${prefix}`;
  const plainSuffix = suffix === undefined ? `` : isLTR ? ` ${suffix}` : `${suffix} `;

  const ltrPlainText = `${plainPrefix}${value}${plainSuffix}`;
  const rtlPlainText = `${plainSuffix}${value}${plainPrefix}`;
  return isLTR ? ltrPlainText : rtlPlainText;
}
